import fetch from 'cross-fetch';

import LRUCache from 'lru-cache';

import { JSONValue } from 'src/shared/types';

export enum HTTPMethod {
    GET = 'GET',
    POST = 'POST',
    PATCH = 'PATCH',
    PUT = 'PUT',
    DELETE = 'DELETE',
}

export type FetchOptions = {
    method?: HTTPMethod;
    headers?: { [key: string]: string };
    body?: string; // stringified json
    preserveEndpoint?: boolean;
    noCache?: boolean;
    signal?: AbortSignal;
    ignoreErrors?: boolean;
    retryCount?: number; // Add retry count option
};

const defaultMethod = HTTPMethod.GET;
const defaultHeaders = { Accept: 'application/json' };
const MAX_RETRY_ATTEMPTS = 2;

export class ApiService {
    private cache: LRUCache<unknown, unknown>;
    private pendingCache: LRUCache<unknown, unknown>;

    public constructor() {
        this.cache = new LRUCache({
            max: 1000,
            maxSize: 5000,
            ttl: 60 * 60 * 1000,
        });

        this.pendingCache = new LRUCache({
            max: 1000,
            maxSize: 5000,
            ttl: 300,
        });
    }

    // method to enable easy testing
    public getFetch() {
        return fetch;
    }

    private buildFetchOptions(options: FetchOptions) {
        const headers: { [key: string]: string } = {
            ...defaultHeaders,
            ...options.headers,
        };
        if (
            options.method &&
            [HTTPMethod.POST, HTTPMethod.PUT, HTTPMethod.PATCH].includes(options.method) &&
            !headers.hasOwnProperty('Content-Type')
        ) {
            headers['Content-Type'] = 'application/json';
        }
        return {
            method: options.method || defaultMethod,
            headers: headers,
            body: options.body,
            signal: options.signal,
        };
    }

    public async fetch(url: string, options: FetchOptions = {}): Promise<any> {
        const retryCount = options.retryCount || 0;

        // Check cache first (skip for retries)
        if (retryCount === 0) {
            const cachedItem = options.noCache ? undefined : this.cache.get(url);
            // if the cached item is null, it means the request has failed, so we return null
            if (cachedItem || cachedItem === null) {
                return cachedItem;
            }
        }

        const fetchOptions = this.buildFetchOptions(options);
        const abortController = new AbortController();
        const signal = abortController.signal;
        fetchOptions.signal = signal;

        // If the url is present in pending cache, and the method is GET, wait for the pending request to complete
        if (!options.noCache && this.pendingCache.has(url) && fetchOptions.method === HTTPMethod.GET && retryCount === 0) {
            // wait until the pending request is completed over a loop
            while (!this.cache.has(url)) {
                await new Promise(resolve => setTimeout(resolve, 500));
            }
            // return the cached response
            return this.cache.get(url);
        }

        try {
            if (!options.noCache && fetchOptions.method === HTTPMethod.GET && retryCount === 0) {
                this.pendingCache.set(url, true, { size: 1 });
            }

            const response = await fetch(url, fetchOptions);

            if (!response.ok) {
                // Handle 401 errors specifically
                if (response.status === 401) {
                    console.warn(`401 Unauthorized error for ${fetchOptions.method} ${url}`);

                    // Don't retry auth endpoints to avoid infinite loops
                    if (url.includes('/login') || url.includes('/logout') || url.includes('/renew')) {
                        throw new Error(`HTTP error! status: ${response.status}`);
                    }

                    // Retry non-auth requests once after token refresh
                    if (retryCount < MAX_RETRY_ATTEMPTS) {
                        console.info(`Retrying request after 401 error (attempt ${retryCount + 1}/${MAX_RETRY_ATTEMPTS})`);
                        await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1 second before retry
                        return this.fetch(url, { ...options, retryCount: retryCount + 1 });
                    }
                }

                this.cache.set(url, null, { size: 1 });
                if (options.ignoreErrors) {
                    return null;
                }
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const processedResponse = await this.handleResponse(response, fetchOptions);

            if (!options.noCache && fetchOptions.method === HTTPMethod.GET) {
                this.cache.set(url, processedResponse, { size: 1 });
            }

            return processedResponse;
        } catch (error: unknown) {
            const errorMessage = (error as Error)?.message || 'Unknown error';
            const isAuthError =
                errorMessage.includes('401') || errorMessage.includes('unauthorized') || errorMessage.includes('authentication');

            if ((error as JSONValue)?.name !== 'AbortError') {
                if (isAuthError) {
                    // Log auth errors as info since they're expected user flow
                    console.info('API authentication error:', {
                        url,
                        method: fetchOptions.method,
                        error: errorMessage,
                        retryCount,
                    });
                } else {
                    // Log other errors normally
                    console.error('Fetch error:', {
                        url,
                        method: fetchOptions.method,
                        error: errorMessage,
                        retryCount,
                    });
                }
            }

            if (!options.noCache && retryCount === 0) {
                this.cache.set(url, error, { size: 1, ttl: 60 * 1000 });
            }

            throw error; // Rethrow the error for the caller to handle
        } finally {
            if (!options.noCache && retryCount === 0) {
                this.pendingCache.delete(url); // Cleanup pending cache entry
            }
        }
    }

    private async handleResponse(response: Response, options: FetchOptions) {
        if (options.headers && options.headers['Accept'] === 'application/json') {
            return await response.json();
        } else {
            return await response.text();
        }
    }
}

const apiService = new ApiService();

export default apiService;
